import { router } from "@inertiajs/react";
import { AlertTriangle, DollarSign, Download, MousePointer, Plus } from "lucide-react";
import { useState } from "react";

import CurrencyFormatHint from "@/components/currency-format-hint";
import LastPurchaseInfo from "@/components/last-purchase-info";
import QuoteItemMatchModal from "@/components/quote-item-match-modal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { exportPriceComparisonPurchaseRequestPath, quotePath } from "@/routes";
import type { LastPurchaseSummary, PriceComparison } from "@/types";

interface PriceComparisonTableProps {
  comparison: PriceComparison;
  purchaseRequestId: number;
  lastPurchasesByRequestItemId?: Record<number, LastPurchaseSummary>;
  onOpenHistory?: (requestItemId: number, requestItemDescription: string) => void;
}

const formatCurrency = (amount: number, currency = "ARS") => {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: currency,
  }).format(amount);
};

const formatPrice = (price: {
  unit_price_ars: number | null;
  unit_price_original?: number;
  currency: string;
}) => {
  if (price.unit_price_ars === null) {
    return <div className="text-muted-foreground text-sm">No disponible</div>;
  }

  const arsFormatted = formatCurrency(price.unit_price_ars, "ARS");

  if (price.currency === "USD" && price.unit_price_original) {
    const usdFormatted = formatCurrency(price.unit_price_original, "USD");
    return (
      <div className="space-y-1">
        <div className="font-medium">{arsFormatted}</div>
        <div className="text-xs text-muted-foreground">({usdFormatted})</div>
      </div>
    );
  }

  return <div className="font-medium">{arsFormatted}</div>;
};

const normalizeMetadataValue = (text?: string | null) => {
  if (typeof text !== "string") return "S/E";
  const trimmed = text.trim().toLowerCase();
  if (trimmed.length === 0 || trimmed === "null") return "S/E";
  return text.trim();
};

// Removed getSortedValidPrices - backend now handles price ranking

function USDConfigDialog({
  comparison,
  isOpen,
  setIsOpen,
}: {
  comparison: PriceComparison;
  isOpen?: boolean;
  setIsOpen?: (open: boolean) => void;
}) {
  const [internalOpen, setInternalOpen] = useState(false);

  // Use internal state if no external control is provided
  const modalOpen = isOpen ?? internalOpen;
  const setModalOpen = setIsOpen ?? setInternalOpen;
  const [exchangeRates, setExchangeRates] = useState<Record<number, string>>(
    {},
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const usdSuppliers = comparison.headers.suppliers.filter(
    (s) => s.currency === "USD",
  );

  if (usdSuppliers.length === 0) return null;

  const handleExchangeRateChange = (quoteId: number, value: string) => {
    setExchangeRates((prev) => ({ ...prev, [quoteId]: value }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      // Update exchange rates sequentially to avoid navigation conflicts
      for (const [quoteId, rate] of Object.entries(exchangeRates)) {
        const numericRate = parseFloat(rate);
        if (numericRate > 0) {
          await new Promise<void>((resolve) => {
            router.patch(
              quotePath(Number(quoteId)),
              { exchange_rate: numericRate },
              {
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => resolve(),
                onError: () => resolve(), // Continue even if one fails
              },
            );
          });
        }
      }

      setModalOpen(false);
      setExchangeRates({});

      // Reload only the price comparison data
      router.reload({ only: ["price_comparison"] });
    } catch (error) {
      console.error("Error updating exchange rates:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const needsConfiguration = usdSuppliers.some(
    (s) => !s.exchange_rate || s.exchange_rate <= 0,
  );

  return (
    <Dialog open={modalOpen} onOpenChange={setModalOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <DollarSign className="w-4 h-4" />
          Configurar USD ({usdSuppliers.length})
          {needsConfiguration && (
            <AlertTriangle className="w-4 h-4 text-amber-500" />
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Configurar Tipos de Cambio USD</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {usdSuppliers.map((supplier) => (
            <div
              key={supplier.id}
              className="flex items-center gap-3 p-3 border rounded-lg"
            >
              <div className="flex-1">
                <div className="font-medium text-sm">{supplier.name}</div>
                <div className="text-xs text-muted-foreground">
                  Quote #{supplier.quote_id}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm">$</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  placeholder={
                    supplier.exchange_rate
                      ? supplier.exchange_rate.toString()
                      : "1200.00"
                  }
                  value={
                    exchangeRates[supplier.quote_id] ??
                    supplier.exchange_rate ??
                    ""
                  }
                  onChange={(e) =>
                    handleExchangeRateChange(supplier.quote_id, e.target.value)
                  }
                  className="w-24 text-right"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          ))}
          <Button
            onClick={() => {
              void handleSubmit();
            }}
            className="w-full"
            disabled={isSubmitting || Object.keys(exchangeRates).length === 0}
          >
            {isSubmitting ? "Aplicando..." : "Aplicar Cambios"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function PriceComparisonTable({
  comparison,
  purchaseRequestId,
  lastPurchasesByRequestItemId = {},
  onOpenHistory,
}: PriceComparisonTableProps) {
  const [usdConfigOpen, setUsdConfigOpen] = useState(false);

  if (!comparison?.rows.length) {
    return null;
  }

  const {
    headers,
    rows,
    available_request_items = [],
    all_quote_items_by_supplier = {},
  } = comparison;

  const requestItemMap = new Map<number, { product_id?: number | null }>(
    available_request_items.map((ri) => [ri.id, { product_id: ri.product_id ?? null }]),
  );

  const handleMatchUpdate = () => {
    // Refresh the page to get updated data
    router.reload();
  };

  const openUsdConfig = () => {
    setUsdConfigOpen(true);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle>Comparativa de Precios</CardTitle>
            <CardDescription>
              Comparación de precios entre proveedores para los items cotizados
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2 sm:justify-end">
            <Button variant="outline" size="sm" asChild className="w-full sm:w-auto">
              <a
                href={exportPriceComparisonPurchaseRequestPath(purchaseRequestId, { format: "pdf" })}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Exportar comparativa
              </a>
            </Button>
            <USDConfigDialog
              comparison={comparison}
              isOpen={usdConfigOpen}
              setIsOpen={setUsdConfigOpen}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-4">
          <CurrencyFormatHint />
        </div>
        {/* Warnings */}
        {comparison.warnings && comparison.warnings.length > 0 && (
          <div className="mb-6 space-y-3">
            {comparison.warnings.map((warning, index) => (
              <div
                key={index}
                className="flex items-center justify-between gap-4 p-4 bg-amber-50 border border-amber-200 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">
                      {warning.message}
                    </p>
                    <p className="text-xs text-amber-700 mt-1">
                      Configura el tipo de cambio para poder comparar precios
                      correctamente
                    </p>
                  </div>
                </div>
                {warning.quote_id && (
                  <Button size="sm" variant="outline" onClick={openUsdConfig}>
                    <DollarSign className="h-4 w-4 mr-2" />
                    Configurar
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="force-scrollbar-x">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[320px]">Artículo</TableHead>
                <TableHead className="text-center min-w-[100px]">
                  Cantidad
                </TableHead>
                <TableHead className="text-center min-w-[80px]">
                  Unidad
                </TableHead>
                {headers.suppliers.map((supplier) => (
                  <TableHead
                    key={supplier.quote_id}
                    className="text-center min-w-[150px]"
                  >
                    <div className="space-y-1">
                      <div className="font-medium">{supplier.name}</div>
                      <Badge variant="outline" className="text-xs">
                        {supplier.currency}
                        {supplier.currency === "USD" &&
                          supplier.exchange_rate &&
                          ` @ ${supplier.exchange_rate}`}
                      </Badge>
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                return (
                  <TableRow key={row.request_item.id}>
                    <TableCell className="font-medium">
                      <div className="space-y-1">
                        <div>{row.request_item.description}</div>
                        <LastPurchaseInfo
                          requestItemId={row.request_item.id}
                          requestItemDescription={row.request_item.description}
                          hasProductAssigned={Boolean(requestItemMap.get(row.request_item.id)?.product_id)}
                          lastPurchase={lastPurchasesByRequestItemId[row.request_item.id]}
                          onOpenHistory={onOpenHistory}
                        />
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {row.request_item.quantity.toLocaleString("es-AR")}
                    </TableCell>
                    <TableCell className="text-center">
                      {row.request_item.unit}
                    </TableCell>
                    {row.prices.map((price, priceIndex) => {
                      const supplier = headers.suppliers[priceIndex];
                      // Backend now provides these flags directly
                      const isBestPrice = price?.is_best_price ?? false;
                      const isSecondBest = price?.is_second_best_price ?? false;

                      return (
                        <TableCell
                          key={supplier.quote_id}
                          className={`text-center ${isBestPrice ? "bg-green-50" : isSecondBest ? "bg-yellow-50" : ""}`}
                        >
                          {price ? (
                            price.missing_exchange_rate ? (
                              <div className="bg-amber-50 border border-amber-200 rounded-md p-2">
                                <div className="flex items-center justify-center gap-1 text-amber-700 mb-1">
                                  <AlertTriangle className="h-3 w-3" />
                                  <span className="text-xs font-medium">
                                    Sin tipo de cambio
                                  </span>
                                </div>
                                <div className="text-sm text-amber-600">
                                  {formatCurrency(
                                    price.unit_price_original ?? 0,
                                    price.currency,
                                  )}
                                </div>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="mt-1 h-6 text-xs"
                                  onClick={openUsdConfig}
                                >
                                  Configurar
                                </Button>
                              </div>
                            ) : (
                              // Regular price - show with re-matching capability
                              <QuoteItemMatchModal
                                requestItemId={row.request_item.id}
                                requestItemDescription={
                                  row.request_item.description
                                }
                                supplierName={supplier.name}
                                allSupplierQuoteItems={
                                  all_quote_items_by_supplier[supplier.id] || []
                                }
                                currentQuoteItemId={price.quote_item_id}
                                onMatchUpdate={handleMatchUpdate}
                              >
                                <div className="cursor-pointer hover:bg-gray-50 p-2 transition-colors relative group">
                                  {formatPrice(price)}
                                  {isBestPrice && (
                                    <Badge
                                      variant="default"
                                      className="mt-1 text-xs bg-green-600"
                                    >
                                      Mejor precio
                                    </Badge>
                                  )}
                                  {/* Show re-match hint on hover - only if there are options */}
                                  {available_request_items.length > 1 && (
                                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/5 rounded-md flex items-center justify-center">
                                      <div className="bg-white shadow-sm border rounded px-2 py-1 text-xs text-gray-600 flex items-center gap-1">
                                        <MousePointer className="h-3 w-3" />
                                        Re-asignar
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </QuoteItemMatchModal>
                            )
                          ) : // Empty cell - show clickable component to assign
                          all_quote_items_by_supplier[supplier.id]?.length >
                            0 ? (
                            <QuoteItemMatchModal
                              requestItemId={row.request_item.id}
                              requestItemDescription={
                                row.request_item.description
                              }
                              supplierName={supplier.name}
                              allSupplierQuoteItems={
                                all_quote_items_by_supplier[supplier.id] || []
                              }
                              currentQuoteItemId={undefined}
                              onMatchUpdate={handleMatchUpdate}
                            >
                              <div className="cursor-pointer hover:bg-blue-50 border-2 border-dashed border-gray-300 hover:border-blue-300 rounded-md p-2 transition-colors">
                                <div className="flex items-center justify-center gap-1 text-gray-500 hover:text-blue-600 transition-colors">
                                  <Plus className="h-4 w-4" />
                                  <span className="text-sm">Asignar</span>
                                </div>
                              </div>
                            </QuoteItemMatchModal>
                          ) : (
                            <div className="text-muted-foreground text-sm">
                              -
                            </div>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}

              {/* Totals Row */}
              {comparison.totals && (
                <TableRow className="border-t-2 border-gray-300 bg-gray-100 font-semibold">
                  <TableCell className="font-bold text-gray-900" colSpan={3}>
                    <span className="text-lg">TOTAL</span>
                  </TableCell>
                  {headers.suppliers.map((supplier) => {
                    const supplierTotal =
                      comparison.totals!.supplier_totals.find(
                        (st) => st.supplier_id === supplier.id,
                      );
                    const isBestTotal =
                      comparison.totals!.best_total_supplier_id === supplier.id;
                    const isSecondBestTotal =
                      comparison.totals!.second_best_total_supplier_id ===
                      supplier.id;

                    return (
                      <TableCell
                        key={supplier.quote_id}
                        className={`text-center font-bold ${
                          isBestTotal
                            ? "bg-green-100"
                            : isSecondBestTotal
                              ? "bg-yellow-100"
                              : "bg-gray-100"
                        }`}
                      >
                        {supplierTotal ? (
                          <div className="space-y-1">
                            <div className="text-lg text-gray-900">
                              {formatCurrency(supplierTotal.total)}
                            </div>
                            {supplierTotal.missing_items > 0 && (
                              <div className="text-xs text-orange-600">
                                Incompleto ({supplierTotal.missing_items} items
                                faltantes)
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-gray-400">-</div>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              )}

              {/* Commercial Terms Rows */}
              <TableRow className="bg-slate-50">
                <TableCell className="font-medium text-slate-700" colSpan={3}>
                  Forma de pago
                </TableCell>
                {headers.suppliers.map((supplier) => (
                  <TableCell
                    key={supplier.quote_id}
                    className="text-center text-sm text-slate-600"
                  >
                    {normalizeMetadataValue(supplier.payment_method)}
                  </TableCell>
                ))}
              </TableRow>

              <TableRow className="bg-slate-50">
                <TableCell className="font-medium text-slate-700" colSpan={3}>
                  Plazo de pago
                </TableCell>
                {headers.suppliers.map((supplier) => {
                  const days = supplier.payment_term_days;
                  const isValid = days != null && days !== "null" && days !== "";
                  return (
                    <TableCell
                      key={supplier.quote_id}
                      className="text-center text-sm text-slate-600"
                    >
                      {isValid ? `${days} días` : "S/E"}
                    </TableCell>
                  );
                })}
              </TableRow>

              <TableRow className="bg-slate-50">
                <TableCell className="font-medium text-slate-700" colSpan={3}>
                  Validez de la oferta
                </TableCell>
                {headers.suppliers.map((supplier) => (
                  <TableCell
                    key={supplier.quote_id}
                    title={normalizeMetadataValue(supplier.validity_text)}
                        className="text-center text-sm text-slate-600 truncate max-w-[50px]"
                  >
                    {normalizeMetadataValue(supplier.validity_text)}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </div>

        {headers.suppliers.some((s) => s.currency === "USD") && (
          <div className="mt-4 text-sm text-muted-foreground">
            <p>
              * Los precios en USD se muestran convertidos a ARS según el tipo
              de cambio configurado.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
